import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from '../../utils/shopHelpers';
import './ShopBannerCarousel.css';

const DEFAULT_SLIDE = {
  id: 'default',
  title: 'The Honeywell CT70: Powering Next-Gen Retail',
  description:
    'Rugged, intuitive, and connected technology that helps your team work smarter and your customers shop better.',
  button_text: 'SHOP HONEYWELL CT70',
  button_link: '/shop',
  image_url: null,
};

function BannerButton({ text, link }) {
  if (!text || !link) return null;
  const isInternal = link.startsWith('/');
  const className = 'shop-banner-carousel__btn';
  if (isInternal) {
    return (
      <Link to={link} className={className}>
        {text}
      </Link>
    );
  }
  return (
    <a href={link} className={className} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

function BannerSlide({ banner, isActive }) {
  const imageUrl = banner.image_url ? resolveImageUrl(banner.image_url, banner.title) : null;
  const hasImage = Boolean(imageUrl);

  return (
    <div
      className={`shop-banner-carousel__slide${isActive ? ' is-active' : ''}${hasImage ? ' has-image' : ''}`}
      aria-hidden={!isActive}
    >
      {hasImage && (
        <>
          <div
            className="shop-banner-carousel__bg"
            style={{ backgroundImage: `url(${imageUrl})` }}
            aria-hidden="true"
          />
          <div className="shop-banner-carousel__scrim" aria-hidden="true" />
        </>
      )}

      <div className="container shop-banner-carousel__layout">
        <div className="shop-banner-carousel__content">
          {banner.title && <h1>{banner.title}</h1>}
          {banner.description && <p>{banner.description}</p>}
          <BannerButton text={banner.button_text} link={banner.button_link} />
        </div>

        {hasImage && (
          <div className="shop-banner-carousel__product" aria-hidden="true">
            <img
              src={imageUrl}
              alt=""
              loading={isActive ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ShopBannerCarousel({ banners = [], loading = false }) {
  const slides = banners.length > 0 ? banners : [DEFAULT_SLIDE];
  const [index, setIndex] = useState(0);
  const hasMultiple = slides.length > 1;

  const goTo = useCallback(
    (nextIndex) => {
      setIndex((nextIndex + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    setIndex(0);
  }, [banners.length]);

  useEffect(() => {
    if (!hasMultiple) return undefined;
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [hasMultiple, slides.length]);

  return (
    <section
      className={`shop-banner-carousel${loading ? ' shop-banner-carousel--loading' : ''}`}
      aria-label="Shop promotions"
      aria-roledescription="carousel"
    >
      <div className="shop-banner-carousel__viewport">
        {slides.map((banner, i) => (
          <BannerSlide key={banner.id ?? i} banner={banner} isActive={i === index} />
        ))}
      </div>

      {hasMultiple && (
        <>
          <button
            type="button"
            className="shop-banner-carousel__arrow shop-banner-carousel__arrow--prev"
            aria-label="Previous slide"
            onClick={() => goTo(index - 1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="shop-banner-carousel__arrow shop-banner-carousel__arrow--next"
            aria-label="Next slide"
            onClick={() => goTo(index + 1)}
          >
            ›
          </button>
          <div className="shop-banner-carousel__dots" role="tablist" aria-label="Banner slides">
            {slides.map((banner, i) => (
              <button
                key={banner.id ?? i}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Slide ${i + 1}`}
                className={`shop-banner-carousel__dot${i === index ? ' is-active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
